import { injectable, interfaces } from 'inversify';
import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import { applyPagination } from '../../../design/pagination/applyPagination';
import { TRAINING_VARIANT } from '../../../modules/jobs/jobs.model';
import { reorderArray } from '../../helpers/array.helpers';
import { sortBy } from '../../helpers/collections.helper';
import { nameOf } from '../../helpers/object.helpers';
import { InputStatus } from '../../models/error.model';
import { IPagingInfo, PaginationInfoDefault } from '../../models/paginationInfo.model';
import { SortingDirection } from '../../models/sortingDirection.model';
import { ProjectStatus } from '../projects/projects.model';
import { ImageAssignmentPolicy, IProjectAvailableDatasetDto, IProjectDataset, IUserDetails } from './projectDetails.models';

export interface IProjectDetailsStore {
  id: string;
  name: string;
  description: string;
  lastAnnotationDate?: string;
  status: ProjectStatus;
  imageAssignmentPolicy: ImageAssignmentPolicy;
  hasImportedAnnotations: boolean;
  modelVariant: TRAINING_VARIANT | null;
  nameStatus: InputStatus;
  descriptionStatus: InputStatus;
  datasetsStatus: InputStatus;
  usersStatus: InputStatus;
  imagesStatus: InputStatus;
  users: IObservableArray<IUserDetails>;
  usersList: IUserDetails[];
  usersPagination: IPagingInfo;
  usersOrderBy: string;
  usersOrderDirection: SortingDirection;
  datasets: IObservableArray<IProjectDataset>;
  availableDatasets: IObservableArray<IProjectAvailableDatasetDto>;
  isReadyForPublish: boolean;
  createdDate: string;
  createdBy: string;
  reorderDatasets(datasetId: string, index: number): void;
}

export namespace IProjectDetailsStore {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsStore> = Symbol('PROJECT_DETAILS_STORE');
}

const defaultUsersSortingBy: string = nameOf<IUserDetails>('email');
const defaultUsersSortingDirection: SortingDirection = SortingDirection.ASC;

@injectable()
export class ProjectDetailsStore implements IProjectDetailsStore {
  id: string = '';

  name: string = '';

  lastAnnotationDate?: string;

  description: string = '';

  status: ProjectStatus = ProjectStatus.Draft;

  hasImportedAnnotations: boolean = false;

  users: IObservableArray<IUserDetails> = observable.array([]);

  // https://github.com/kulshekhar/ts-jest/pull/308/files
  imageAssignmentPolicy: ImageAssignmentPolicy = 'Random' as ImageAssignmentPolicy;

  datasets: IObservableArray<IProjectDataset> = observable.array([]);

  usersPagination: IPagingInfo = PaginationInfoDefault;

  availableDatasets: IObservableArray<IProjectAvailableDatasetDto> = observable.array([]);

  nameStatus: InputStatus = InputStatus.empty();

  descriptionStatus: InputStatus = InputStatus.empty();

  datasetsStatus: InputStatus = InputStatus.empty();

  usersStatus: InputStatus = InputStatus.empty();

  imagesStatus: InputStatus = InputStatus.empty();

  usersOrderBy: string = defaultUsersSortingBy;

  usersOrderDirection: SortingDirection = defaultUsersSortingDirection;

  createdDate: string = new Date().toISOString();

  createdBy: string = '';

  modelVariant: TRAINING_VARIANT | null = null;

  isReadyForPublish: boolean = true;

  constructor() {
    makeObservable(this, {
      id: observable,
      name: observable,
      lastAnnotationDate: observable,
      description: observable,
      status: observable,
      hasImportedAnnotations: observable,
      users: observable,
      imageAssignmentPolicy: observable,
      datasets: observable,
      usersPagination: observable,
      availableDatasets: observable,
      nameStatus: observable,
      descriptionStatus: observable,
      datasetsStatus: observable,
      usersStatus: observable,
      imagesStatus: observable,
      usersOrderBy: observable,
      usersOrderDirection: observable,
      createdDate: observable,
      modelVariant: observable,
      reorderDatasets: action,
      usersList: computed,
    });
  }

  reorderDatasets(datasetId: string, index: number): void {
    const order = this.datasets[index].order;
    const ordered = reorderArray(this.datasets, datasetId, order);

    this.datasets.replace([...ordered]);
  }

  get usersList(): IUserDetails[] {
    const sorted = sortBy(this.users, this.usersOrderBy, this.usersOrderDirection, defaultUsersSortingBy, defaultUsersSortingDirection);
    return applyPagination(sorted, this.usersPagination);
  }
}
